import { useState } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track } from 'modules/core/lib/Tracking'

import { findValidAM } from 'modules/onboarding/lib/hubspot/meetings'

interface MeetingData {
  meetingDate?: string
  meetingDuration?: string
  meetingBookSucceeded?: boolean
  meetingBookFailed?: boolean
}

interface MeetingPayload {
  meetingBookSucceeded: boolean
  meetingBookFailed: boolean
  meetingsPayload: {
    bookingResponse: {
      event: {
        duration: number
        dateString: string
        dateTime: number
      }
    }
    userSlug?: string
  }
}

const useMeetingData = ({ onSuccessEvent }: { onSuccessEvent: string }) => {
  const { company } = useCurrentSession()
  const [accountManagerName, setAccountManagerName] = useState<string | null>(null)
  const [meetingData, setMeetingData] = useLocalStorage<Partial<MeetingData>>(
    LOCAL_STORAGE_KEYS.upsellMeetingData,
    {
      meetingBookSucceeded: false,
      meetingDate: undefined,
    }
  )

  const getFormattedName = (fullName: string) => {
    const firstName = fullName.split('-')[0]
    if (!firstName) return null

    return firstName.charAt(0).toUpperCase() + firstName.slice(1)
  }

  /*
    Rules:
    - Show the calendar if the company has an account manager
    - Show the calendar if there is no meeting booked or is previous than today
  */
  const shouldShowCalendar = async () => {
    if (meetingData?.meetingDate) {
      const meetingDate = new Date(meetingData.meetingDate)
      const today = new Date()

      return meetingDate < today
    }

    const validAM = await findValidAM(company.id.toString(), false)
    if (validAM) setAccountManagerName(getFormattedName(validAM))

    return !!validAM
  }

  const writeData = (newData: Partial<MeetingData>) => {
    setMeetingData({ ...meetingData, ...newData })
  }

  const onMeetingBooked = (data: MeetingPayload) => {
    if (!data || !data.meetingsPayload) return
    let meeting: MeetingData

    if (data.meetingBookSucceeded) {
      const duration = Math.floor(
        (data.meetingsPayload?.bookingResponse.event.duration / 1000 / 60) % 60
      ).toString()
      const meetingDate = new Date(
        data.meetingsPayload.bookingResponse.event.dateTime
      ).toISOString()

      meeting = {
        meetingDate: meetingDate,
        meetingDuration: duration,
        meetingBookSucceeded: data.meetingBookSucceeded,
      }

      writeData(meeting)
      track(onSuccessEvent, {
        am_slug: data.meetingsPayload.userSlug,
        meeting_date: meetingDate,
      })
    }
    if (data.meetingBookFailed) {
      meeting = {
        meetingBookFailed: data.meetingBookFailed,
      }
      writeData(meeting)
    }
  }

  return {
    meetingData,
    writeData,
    shouldShowCalendar,
    onMeetingBooked,
    accountManagerName,
  }
}

export default useMeetingData
